import {Injectable} from '@angular/core';
import {BehaviorSubject, Observable} from "rxjs";
import {HttpClient} from "@angular/common/http";
import {distinctUntilChanged, filter, first, map, shareReplay, startWith, switchMap, tap} from "rxjs/operators";
import {MsalBroadcastService, MsalService} from "@azure/msal-angular";
import {EventMessage, EventType} from "@azure/msal-browser";
import {AuthenticatedPersonModel} from "../models/person/authenticated-person.model";
import {LoadingService} from "./loading.service";
import {AuthenticationResult} from "@azure/msal-common";
import {Configuration} from "../../assets/Configuration";
import {environment, protectedResources} from "../../environments/environment";

@Injectable({
    providedIn: 'root'
})
export class AuthorizationService {

    private currentPersonSubject = new BehaviorSubject<AuthenticatedPersonModel | null>(null);
    private currentPerson$: Observable<AuthenticatedPersonModel> = this.currentPersonSubject.asObservable()
        .pipe(
            filter(person => person != null),
            map(person => person as AuthenticatedPersonModel)
        );
    private cachedPerson$?: Observable<AuthenticatedPersonModel> | null;

    private accessTokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);
    public accessToken$ = this.accessTokenSubject.asObservable();

    private loginInitiated = new BehaviorSubject<boolean>(false);
    public loginInitiated$ = this.loginInitiated.asObservable();

    public isAuthenticated$ = this.msalBroadcastService.msalSubject$.pipe(
        startWith(null),
        map(() => this.authService.instance.getAllAccounts().length > 0),
        distinctUntilChanged(),
        shareReplay(1)
    );

    constructor(
        private httpClient: HttpClient,
        private msalBroadcastService: MsalBroadcastService,
        private loadingService: LoadingService,
        private authService: MsalService
    ) {
        this.msalBroadcastService.msalSubject$
            .pipe(
                tap(ev => console.log("ev", ev)),
                filter((msg: EventMessage) => msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS),
                map((msg: EventMessage) => msg.payload as AuthenticationResult)
            ).subscribe(payload => {
            this.accessTokenSubject.next(payload.accessToken);
        });

        this.msalBroadcastService.msalSubject$
            .pipe(filter((msg: EventMessage) => msg.eventType === EventType.HANDLE_REDIRECT_START),)
            .subscribe(() => {
                    this.loginInitiated.next(true);
                }
            );

        this.msalBroadcastService.msalSubject$
            .pipe(filter((msg: EventMessage) => msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS || msg.eventType === EventType.ACQUIRE_TOKEN_FAILURE))
            .subscribe(() => {
                    this.loginInitiated.next(false);
                }
            );

        this.msalBroadcastService.msalSubject$
            .pipe(filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_SUCCESS),)
            .subscribe(() => {
                    this.currentPersonSubject.next(null);
                    if (!this.authService.instance.getActiveAccount() && this.authService.instance.getAllAccounts().length > 0) {
                        this.authService.instance.setActiveAccount(this.authService.instance.getAllAccounts()[0]);
                    }
                    this.authService.acquireTokenSilent({scopes: protectedResources.scopes});
                }
            );
    }

    public getCurrentPerson(): Observable<AuthenticatedPersonModel> {
        if (this.currentPersonSubject.value == null) {
            this.loadingService.load(this.httpClient.get<AuthenticatedPersonModel>(Configuration.getApiBaseUrl() + "/person/current"))
                .subscribe(currentPersonResult => this.currentPersonSubject.next(currentPersonResult));
        }
        return this.currentPerson$;
    }

    public reloadCurrentPerson(): void {
        this.loadingService.load(this.httpClient.get<AuthenticatedPersonModel>(Configuration.getApiBaseUrl() + "/person/current"))
            .subscribe(currentPersonResult => this.currentPersonSubject.next(currentPersonResult));
    }

    public changePassword(data: { password: string }) {
        return this.currentPerson$.pipe(first(), switchMap((person) =>
            this.loadingService.load(
                this.httpClient.post<AuthenticatedPersonModel>(
                    Configuration.getApiBaseUrl() + "/User/change-password",
                    {
                        personId: person.person.id,
                        newPassword: data.password
                    },
                )
            )
        ))
    }

    get isAuthenticatedSnapshot(): boolean {
        return this.authService.instance.getAllAccounts().length > 0;
    }

    logout(): void {
        this.authService.logout();
        this.accessTokenSubject.next(null);
        this.loginInitiated.next(false);
    }

    login(): void {
        this.authService.loginRedirect({
            scopes: protectedResources.scopes,
            domainHint: environment.domainHint ? environment.domainHint : undefined,
        });
    }

    loginWithCompanyAccount(domainHint: string): void {
        const hint = domainHint.trim();
        if (!hint) {
            throw new Error('Company login requires a federation domain hint.');
        }

        this.authService.loginRedirect({
            scopes: protectedResources.scopes,
            domainHint: hint,
        });
    }
}
